import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useTRPC, type RouterOutputs } from "@/trpc";

export type WorkspaceInvitation =
  RouterOutputs["providerWorkspace"]["listInvitations"]["invitations"][number];

export function WorkspaceInvitationEmailEditor({
  invitation,
  organizationBusinessUnitId,
  onClose,
}: {
  invitation: WorkspaceInvitation;
  organizationBusinessUnitId: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  // Freeze the invitation and version together while the manager refetches.
  const [snapshot] = useState(invitation);
  const [email, setEmail] = useState(snapshot.email ?? "");
  const [error, setError] = useState<string | null>(null);
  const changed =
    email.trim().toLowerCase() !== (snapshot.email ?? "").toLowerCase();
  const save = useMutation(
    trpc.providerWorkspace.updateInvitationEmail.mutationOptions({
      onSuccess: async (_, variables) => {
        const input = {
          organizationBusinessUnitId: variables.organizationBusinessUnitId,
        };
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.providerWorkspace.listInvitations.queryKey(input),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.providerWorkspace.rosterDetails.queryKey(input),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.organizationBusinessUnitAffiliation.listInvitations.queryKey(
                input,
              ),
          }),
        ]);
        toast.success(
          changed
            ? "Email updated. Send a new invitation when ready."
            : "Invitation email saved.",
        );
        onClose();
      },
      onError: (failure) => setError(failure.message),
    }),
  );
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit invitation email</DialogTitle>
          <DialogDescription>
            Update the address used for this invitation. Saving does not send an
            email.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            const result = z.string().email().safeParse(email.trim());
            if (!result.success) {
              setError("Enter a valid email address.");
              return;
            }
            setError(null);
            save.mutate({
              organizationBusinessUnitId,
              invitationId: snapshot.id,
              expectedVersion: snapshot.expectedVersion,
              email: result.data,
            });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="workspace-invitation-email">Invitation email</Label>
            <Input
              id="workspace-invitation-email"
              type="email"
              value={email}
              disabled={save.isPending}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          {changed && snapshot.inviteSentAt !== null && (
            <p className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Changing the address cancels the invitation previously sent to{" "}
              {snapshot.email}. Send a new invitation after saving.
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
